import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  ITenantsRepository,
  Tenant,
  CreateTenantInput,
  ProPortalTenant,
  PublicCompanyIdentity,
  PublicProPdfTemplate,
  Workspace,
  PendingInvitation,
} from '@erp/data-access/interfaces';
import { onboardingProfileSchema } from '@erp/domain/onboarding';

interface TenantRow {
  id: string;
  name: string;
  subdomain: string;
  custom_domain: string | null;
  mf: string | null;
  phone: string | null;
  email: string | null;
  owner_user_id: string;
  plan: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Implementation of ITenantsRepository against the master Supabase project.
 *  All queries hit the same `tenants` table; RLS handles the authentication
 *  context (owner sees their tenant, anon can see active tenants for
 *  hostname resolution from the storefront). */
export class SupabaseTenantsRepository implements ITenantsRepository {
  constructor(private supabase: SupabaseClient) {}

  async getCurrent(): Promise<Tenant | null> {
    // For regular users RLS already scopes tenants to their own tenant_id,
    // so a `limit-1` would be enough. But platform admins see every
    // tenant — `limit(1)` would silently return whichever row Postgres
    // picks first, masking which tenant the rest of the app is operating
    // as. Filter explicitly by current_user_tenant_id() so admin and
    // regular users converge on the same tenant the BEFORE-INSERT
    // triggers will write to.
    type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: string | null; error: { message: string } | null }> };
    const { data: tenantId, error: tenantResolutionError } = await (
      this.supabase as unknown as SbAny
    ).rpc('current_user_tenant_id', {});
    if (tenantResolutionError) throw tenantResolutionError;
    if (!tenantId) return null;
    const { data, error } = await this.supabase
      .from('tenants')
      .select('*')
      .eq('id', tenantId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapTenant(data as TenantRow) : null;
  }

  /** Résolution d'hôte pour la vitrine publique, via le RPC
   *  `resolve_storefront_tenant` plutôt qu'une lecture de `tenants`.
   *
   *  La lecture directe (`getByHostname` ci-dessous) exigeait une policy
   *  `anon` sur toute la table : la RLS filtre les lignes, pas les colonnes,
   *  si bien que la clé anon — publique, elle est dans le bundle — donnait
   *  le matricule fiscal, le téléphone et l'e-mail des 21 marchands. Le RPC
   *  ne rend qu'un identifiant, ce dont l'appelant a réellement besoin. */
  async resolveIdByHostname(host: string): Promise<string | null> {
    const cleaned = host.trim().toLowerCase();
    if (!cleaned) return null;
    type SbRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: { tenant_id: string }[] | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbRpc).rpc(
      'resolve_storefront_tenant',
      { host: cleaned },
    );
    if (error) throw new Error(error.message);
    return data?.[0]?.tenant_id ?? null;
  }

  async getByHostname(host: string): Promise<Tenant | null> {
    const cleaned = host.trim().toLowerCase().replace(/^www\./, '');
    // Try the leftmost label as a Lyfty subdomain first (the common case),
    // then fall back to the full host as a custom domain. Two requests
    // sequentially so an "or" filter doesn't fight RLS predicates.
    const subdomain = cleaned.split('.')[0];

    // Try subdomain match
    const sub = await this.supabase
      .from('tenants')
      .select('*')
      .ilike('subdomain', subdomain ?? '')
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    if (sub.data) return mapTenant(sub.data as TenantRow);

    // Try custom domain match
    const custom = await this.supabase
      .from('tenants')
      .select('*')
      .or(`custom_domain.ilike.${cleaned},custom_domain.ilike.www.${cleaned}`)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    if (custom.data) return mapTenant(custom.data as TenantRow);

    return null;
  }

  async getProPortalTenant(subdomain: string): Promise<ProPortalTenant | null> {
    const clean = subdomain.trim().toLowerCase();
    if (!clean) return null;
    interface Row { id: string; name: string; subdomain: string }
    // SECURITY DEFINER RPC — it applies the `pro` module check itself, so a
    // tenant without Espace Pro simply yields no row. Do NOT swap this for a
    // `tenants` read: that table is anon-readable for every active tenant.
    const { data, error } = await this.supabase
      .rpc('pro_portal_tenant', { p_subdomain: clean })
      .maybeSingle<Row>();
    if (error) throw error;
    if (!data) return null;
    return { id: data.id, name: data.name, subdomain: data.subdomain };
  }

  async getPublicCompanyIdentity(tenantId: string): Promise<PublicCompanyIdentity | null> {
    interface IdentityRow {
      company_name: string | null; tax_id: string | null; address: string | null;
      phone: string | null; email: string | null; fax: string | null; website: string | null;
      rib: string | null; bank_name: string | null; tagline: string | null; subtitle: string | null;
      social_facebook: string | null; social_instagram: string | null; social_linkedin: string | null;
      pdf_logo_url: string | null; logo_url: string | null;
    }
    type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: IdentityRow[] | null; error: { message: string } | null }> };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'pro_public_company_identity',
      { p_tenant_id: tenantId },
    );
    if (error) throw new Error(error.message);
    const row = data?.[0];
    if (!row) return null;
    return {
      companyName: row.company_name ?? '',
      taxId: row.tax_id ?? null,
      address: row.address ?? null,
      phone: row.phone ?? null,
      email: row.email ?? null,
      fax: row.fax ?? null,
      website: row.website ?? null,
      rib: row.rib ?? null,
      bankName: row.bank_name ?? null,
      tagline: row.tagline ?? null,
      subtitle: row.subtitle ?? null,
      socialFacebook: row.social_facebook ?? null,
      socialInstagram: row.social_instagram ?? null,
      socialLinkedin: row.social_linkedin ?? null,
      pdfLogoUrl: row.pdf_logo_url ?? null,
      logoUrl: row.logo_url ?? null,
    };
  }

  async getPublicProPdfTemplate(tenantId: string): Promise<PublicProPdfTemplate | null> {
    interface TemplateRow { design_key: string | null; options: Record<string, unknown> | null }
    type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: TemplateRow[] | null; error: { message: string } | null }> };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'pro_public_pdf_template',
      { p_tenant_id: tenantId },
    );
    if (error) throw new Error(error.message);
    const row = data?.[0];
    if (!row?.design_key) return null;
    const known: PublicProPdfTemplate['designKey'][] = ['classic', 'modern', 'minimal', 'tunisien'];
    const designKey = known.find((k) => k === row.design_key);
    if (!designKey) return null;
    return { designKey, options: row.options ?? {} };
  }

  async create(input: CreateTenantInput): Promise<{ tenantId: string }> {
    // The RPC normalises and trims everything server-side; we only
    // pre-clean the keys we forward as JSON so the wizard's blank
    // strings turn into real `null`s. (Server-side `NULLIF(trim(...))`
    // also handles this, but keeping the wire payload clean makes the
    // PostgREST request log readable.)
    const detailsRaw = input.details ?? {};
    const details: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(detailsRaw)) {
      if (typeof v === 'string') {
        const t = v.trim();
        if (t.length > 0) details[k] = t;
      } else if (v != null) {
        // Stringify non-string non-null values defensively (shouldn't happen).
        details[k] = String(v);
      }
    }

    const commonArgs = {
      p_name: input.name,
      p_subdomain: input.subdomain,
      p_mf: input.mf ?? null,
      p_phone: input.phone ?? null,
      p_email: input.email ?? null,
      p_details: details,
      p_modules: input.modules ?? [],
      p_theme_name: input.themeName ?? null,
    };

    // New onboarding writes use the wrapper so the tenant and its discovery
    // profile commit or roll back together. Keep the canonical RPC as a
    // compatibility path for internal callers that do not run the questionnaire.
    const profile = input.onboardingProfile
      ? onboardingProfileSchema.parse(input.onboardingProfile)
      : null;
    type CreateTenantRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };
    const { data, error } = await (
      this.supabase as unknown as CreateTenantRpc
    ).rpc(
      profile
        ? 'create_tenant_with_onboarding'
        : 'create_tenant_for_current_user',
      profile
        ? {
            ...commonArgs,
            p_onboarding: {
              ...profile,
              // Rolling-deploy compatibility: the previous database function
              // reads one scalar. The additive migration prefers the array and
              // still dual-writes this first answer for older clients.
              primaryGoal: profile.primaryGoals[0],
            },
          }
        : commonArgs,
    );
    if (error) throw error;
    return { tenantId: data as string };
  }

  async isSubdomainTaken(subdomain: string): Promise<boolean> {
    const v = subdomain.trim().toLowerCase();
    if (!v) return false;
    // Call the SECURITY DEFINER RPC — a plain SELECT on `tenants` is
    // hidden by RLS for users who don't yet have a tenant_member row
    // (the entire onboarding audience), so it would falsely report
    // "available" for every subdomain. See migration 20260426100000.
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: boolean | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'is_subdomain_taken',
      { p_subdomain: v },
    );
    if (error) throw error;
    return data === true;
  }

  async listMyWorkspaces(): Promise<Workspace[]> {
    interface Row {
      tenant_id: string;
      name: string | null;
      subdomain: string | null;
      role_slug: string | null;
      role_name: string | null;
      is_owner: boolean | null;
      logo_url: string | null;
      is_active: boolean | null;
      joined_at: string;
    }
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: Row[] | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'list_my_workspaces',
      {},
    );
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => ({
      tenantId: row.tenant_id,
      name: row.name ?? '',
      subdomain: row.subdomain ?? '',
      roleSlug: row.role_slug,
      roleName: row.role_name,
      isOwner: row.is_owner === true,
      logoUrl: row.logo_url,
      isActive: row.is_active !== false,
      joinedAt: row.joined_at,
    }));
  }

  async listMyPendingInvitations(): Promise<PendingInvitation[]> {
    interface Row {
      invitation_id: string;
      tenant_id: string;
      tenant_name: string | null;
      role_name: string | null;
      inviter_email: string | null;
      expires_at: string;
      created_at: string;
    }
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: Row[] | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'list_my_pending_invitations',
      {},
    );
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => ({
      invitationId: row.invitation_id,
      tenantId: row.tenant_id,
      tenantName: row.tenant_name ?? '',
      roleName: row.role_name,
      inviterEmail: row.inviter_email,
      expiresAt: row.expires_at,
      createdAt: row.created_at,
    }));
  }

  async acceptMyInvitation(invitationId: string): Promise<{ tenantId: string }> {
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'accept_my_invitation',
      { p_invitation_id: invitationId },
    );
    if (error) throw new Error(error.message);
    if (!data) throw new Error('invitation_not_found');
    return { tenantId: data };
  }

  async leaveWorkspace(tenantId: string): Promise<void> {
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };
    const { error } = await (this.supabase as unknown as SbAny).rpc('leave_tenant', {
      p_tenant_id: tenantId,
    });
    // On laisse remonter le code brut (`owner_cannot_leave`, `not_a_member`) :
    // l'UI le traduit, comme pour les invitations.
    if (error) throw new Error(error.message);
  }

  async canCreateAdditionalWorkspace(): Promise<boolean> {
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: boolean | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'can_create_additional_workspace',
      {},
    );
    // Fail CLOSED: si la vérification échoue on n'affiche pas le bouton
    // « créer une société ». Le serveur refuserait de toute façon, mais
    // proposer une action qui va échouer est pire que ne pas la proposer.
    if (error) return false;
    return data === true;
  }
}

function mapTenant(row: TenantRow): Tenant {
  return {
    id: row.id,
    name: row.name,
    subdomain: row.subdomain,
    customDomain: row.custom_domain,
    mf: row.mf,
    phone: row.phone,
    email: row.email,
    ownerUserId: row.owner_user_id,
    plan: row.plan,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
